import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  CROP_ZOOM_STEP,
  clampCropOffset,
  clampCropZoom,
  coverScale,
  pinchDistance,
} from '../../util/photoCrop';
import Button, { SecondaryButton } from '../Button/Button';

import css from './PhotoCropModal.module.css';

const OUTPUT_SIZE = 1200;

const readImage = file =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => resolve({ image, url });
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read photo'));
    };
    image.src = url;
  });

const cropToFile = ({ image, file, frame, offset, zoom, aspectWidth, aspectHeight }) => {
  const canvas = document.createElement('canvas');
  const ratio = aspectWidth / aspectHeight;
  const width = OUTPUT_SIZE;
  const height = Math.round(OUTPUT_SIZE / ratio);
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  const scale =
    coverScale(image.naturalWidth, image.naturalHeight, frame.width, frame.height) *
    clampCropZoom(zoom);
  const drawnWidth = image.naturalWidth * scale;
  const drawnHeight = image.naturalHeight * scale;
  const sx =
    ((drawnWidth - frame.width) / 2 - offset.x) *
    (image.naturalWidth / drawnWidth);
  const sy =
    ((drawnHeight - frame.height) / 2 - offset.y) *
    (image.naturalHeight / drawnHeight);
  const sw = frame.width * (image.naturalWidth / drawnWidth);
  const sh = frame.height * (image.naturalHeight / drawnHeight);
  ctx.drawImage(image, sx, sy, sw, sh, 0, 0, width, height);
  return new Promise(resolve => {
    canvas.toBlob(
      blob => {
        const next = new File([blob], file.name || 'profile-photo.jpg', {
          type: blob?.type || 'image/jpeg',
        });
        resolve(next);
      },
      'image/jpeg',
      0.9
    );
  });
};

/**
 * Mobile-first crop overlay: pinch plus zoom buttons while framing a photo.
 *
 * @param {Object} props
 * @param {File} props.file
 * @param {number} props.aspectWidth
 * @param {number} props.aspectHeight
 * @param {Function} props.onCancel
 * @param {Function} props.onConfirm
 * @param {boolean} [props.circular] - Circle mask for avatar / profile photos
 * @returns {JSX.Element|null}
 */
const PhotoCropModal = props => {
  const { file, aspectWidth = 1, aspectHeight = 1, circular = false, onCancel, onConfirm } = props;
  const intl = useIntl();
  const frameRef = useRef(null);
  const pinchStart = useRef(null);
  const [preview, setPreview] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [drag, setDrag] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let revoked = false;
    readImage(file)
      .then(({ image, url }) => {
        if (!revoked) {
          setPreview({ image, url });
        } else {
          URL.revokeObjectURL(url);
        }
      })
      .catch(() => {
        if (!revoked) {
          onCancel();
        }
      });
    return () => {
      revoked = true;
    };
  }, [file, onCancel]);

  useEffect(() => {
    return () => {
      if (preview?.url) {
        URL.revokeObjectURL(preview.url);
      }
    };
  }, [preview?.url]);

  const frameSize = () => {
    const rect = frameRef.current?.getBoundingClientRect();
    return { width: rect?.width || 1, height: rect?.height || 1 };
  };

  const applyZoom = nextZoom => {
    const frame = frameSize();
    const clamped = clampCropZoom(nextZoom);
    setZoom(clamped);
    setOffset(current =>
      clampCropOffset({
        ...current,
        imageWidth: preview?.image?.naturalWidth,
        imageHeight: preview?.image?.naturalHeight,
        frameWidth: frame.width,
        frameHeight: frame.height,
        zoom: clamped,
      })
    );
  };

  const onPointerDown = event => {
    if (event.touches?.length === 2) {
      pinchStart.current = {
        distance: pinchDistance(event.touches[0], event.touches[1]),
        zoom,
      };
      setDrag(null);
      return;
    }
    const point = event.touches?.[0] || event;
    setDrag({ x: point.clientX - offset.x, y: point.clientY - offset.y });
  };

  const onPointerMove = event => {
    if (event.touches?.length === 2) {
      event.preventDefault();
      const start = pinchStart.current;
      if (!start?.distance) {
        pinchStart.current = {
          distance: pinchDistance(event.touches[0], event.touches[1]),
          zoom,
        };
        return;
      }
      const next = start.zoom * (pinchDistance(event.touches[0], event.touches[1]) / start.distance);
      applyZoom(next);
      return;
    }
    if (!drag) {
      return;
    }
    const point = event.touches?.[0] || event;
    const frame = frameSize();
    setOffset(
      clampCropOffset({
        x: point.clientX - drag.x,
        y: point.clientY - drag.y,
        imageWidth: preview?.image?.naturalWidth,
        imageHeight: preview?.image?.naturalHeight,
        frameWidth: frame.width,
        frameHeight: frame.height,
        zoom,
      })
    );
  };

  const endGesture = () => {
    setDrag(null);
    pinchStart.current = null;
  };

  const confirm = async () => {
    if (!preview?.image) {
      return;
    }
    setBusy(true);
    try {
      const cropped = await cropToFile({
        image: preview.image,
        file,
        frame: frameSize(),
        offset,
        zoom,
        aspectWidth,
        aspectHeight,
      });
      onConfirm(cropped);
    } finally {
      setBusy(false);
    }
  };

  const scale = preview
    ? coverScale(
        preview.image.naturalWidth,
        preview.image.naturalHeight,
        frameSize().width,
        frameSize().height
      ) * zoom
    : 1;

  return (
    <div className={css.root} role="dialog" aria-modal="true" aria-labelledby="photo-crop-title">
      <div className={css.sheet}>
        <h2 id="photo-crop-title" className={css.title}>
          <FormattedMessage id="PhotoCropModal.title" />
        </h2>
        <p className={css.hint}>
          <FormattedMessage id="PhotoCropModal.hint" />
        </p>
        <div
          ref={frameRef}
          className={classNames(css.frame, { [css.circle]: circular })}
          data-crop-shape={circular ? 'circle' : 'rect'}
          style={{ aspectRatio: `${aspectWidth} / ${aspectHeight}` }}
          onMouseDown={onPointerDown}
          onMouseMove={onPointerMove}
          onMouseUp={endGesture}
          onMouseLeave={endGesture}
          onTouchStart={onPointerDown}
          onTouchMove={onPointerMove}
          onTouchEnd={endGesture}
        >
          {preview ? (
            <img
              src={preview.url}
              alt={intl.formatMessage({ id: 'PhotoCropModal.previewAlt' })}
              className={css.image}
              style={{
                transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${scale})`,
              }}
              draggable={false}
            />
          ) : (
            <p className={css.loading}>
              <FormattedMessage id="PhotoCropModal.loading" />
            </p>
          )}
        </div>
        <div className={css.zoomRow}>
          <button
            type="button"
            className={classNames(css.zoomButton)}
            onClick={() => applyZoom(zoom - CROP_ZOOM_STEP)}
            aria-label={intl.formatMessage({ id: 'PhotoCropModal.zoomOut' })}
          >
            −
          </button>
          <input
            className={css.slider}
            type="range"
            min="1"
            max="4"
            step="0.05"
            value={zoom}
            onChange={event => applyZoom(Number(event.target.value))}
            aria-label={intl.formatMessage({ id: 'PhotoCropModal.zoomSlider' })}
          />
          <button
            type="button"
            className={css.zoomButton}
            onClick={() => applyZoom(zoom + CROP_ZOOM_STEP)}
            aria-label={intl.formatMessage({ id: 'PhotoCropModal.zoomIn' })}
          >
            +
          </button>
        </div>
        <div className={css.actions}>
          <SecondaryButton type="button" onClick={onCancel} disabled={busy}>
            <FormattedMessage id="PhotoCropModal.cancel" />
          </SecondaryButton>
          <Button type="button" onClick={confirm} inProgress={busy} disabled={!preview}>
            <FormattedMessage id="PhotoCropModal.usePhoto" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PhotoCropModal;
